
import { useState } from 'react';

interface DemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  demoType?: string;
}

const DemoModal = ({ isOpen, onClose, demoType = '' }: DemoModalProps) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    sector: '',
    appointment_type: demoType,
    preferred_date: '',
    preferred_time: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear status when user starts typing
    if (submitStatus.type) {
      setSubmitStatus({ type: null, message: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      setSubmitStatus({
        type: 'error',
        message: 'Mesaj 500 karakteri geçemez.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('sector', formData.sector);
      formDataToSend.append('appointment_type', formData.appointment_type);
      formDataToSend.append('preferred_date', formData.preferred_date);
      formDataToSend.append('preferred_time', formData.preferred_time);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/submit/randevu-formu', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: 'Randevu talebiniz başarıyla gönderildi! En kısa sürede sizinle iletişime geçeceğiz.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          sector: '',
          appointment_type: demoType,
          preferred_date: '',
          preferred_time: '',
          message: ''
        });
        // Close modal after 3 seconds
        setTimeout(() => {
          onClose();
          setSubmitStatus({ type: null, message: '' });
        }, 3000);
      } else {
        setSubmitStatus({
          type: 'error',
          message: 'Bir hata oluştu. Lütfen tekrar deneyin.'
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      sector: '',
      appointment_type: demoType,
      preferred_date: '',
      preferred_time: '',
      message: ''
    });
    setSubmitStatus({ type: null, message: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Randevu Al</h2>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200"
            >
              <i className="ri-close-line text-xl text-gray-500"></i>
            </button>
          </div>

          <p className="text-gray-600 mb-6">
            Webcoll çözümlerini canlı görmek ve projenizi konuşmak için randevu alın. Size özel demo hazırlayalım.
          </p>

          {/* Status Message */}
          {submitStatus.type && (
            <div className={`mb-6 p-4 rounded-xl ${
              submitStatus.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <div className="flex items-center">
                <i className={`${
                  submitStatus.type === 'success' ? 'ri-check-circle-line' : 'ri-error-warning-line'
                } mr-2 text-lg`}></i>
                {submitStatus.message}
              </div>
            </div>
          )}

          {/* Form */}
          <form id="randevu-formu" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="appointment-name" className="block text-sm font-semibold text-gray-700 mb-2">
                Ad Soyad *
              </label>
              <input
                type="text"
                id="appointment-name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="Adınız ve soyadınız"
              />
            </div>

            <div>
              <label htmlFor="appointment-email" className="block text-sm font-semibold text-gray-700 mb-2">
                E-posta *
              </label>
              <input
                type="email"
                id="appointment-email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="ornek@email.com"
              />
            </div>

            <div>
              <label htmlFor="appointment-phone" className="block text-sm font-semibold text-gray-700 mb-2">
                Telefon *
              </label>
              <input
                type="tel"
                id="appointment-phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="0545 148 59 52"
              />
            </div>

            <div>
              <label htmlFor="appointment-sector" className="block text-sm font-semibold text-gray-700 mb-2">
                Sektör
              </label>
              <select
                id="appointment-sector"
                name="sector"
                value={formData.sector}
                onChange={handleInputChange}
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm pr-8 disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                <option value="">Sektörünüzü seçin</option>
                <option value="restoran">Restoran & Kafe</option>
                <option value="otomotiv">Otomotiv & Galeri</option>
                <option value="guzellik">Güzellik & Sağlık</option>
                <option value="eticaret">E-Ticaret</option>
                <option value="danismanlik">Danışmanlık & Finans</option>
                <option value="avukat">Avukat & Doktor</option>
                <option value="diger">Diğer</option>
              </select>
            </div>

            <div>
              <label htmlFor="appointment-type" className="block text-sm font-semibold text-gray-700 mb-2">
                Randevu Türü
              </label>
              <select
                id="appointment-type"
                name="appointment_type"
                value={formData.appointment_type}
                onChange={handleInputChange}
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm pr-8 disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                <option value="">Randevu türünü seçin</option>
                <option value="canlı-demo">Canlı Demo</option>
                <option value="proje-danışmanlığı">Proje Danışmanlığı</option>
                <option value="fiyat-teklifi">Fiyat Teklifi</option>
                <option value="teknik-destek">Teknik Destek</option>
                <option value="genel-bilgi">Genel Bilgi</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="preferred-date" className="block text-sm font-semibold text-gray-700 mb-2">
                  Tercih Edilen Tarih
                </label>
                <input
                  type="date"
                  id="preferred-date"
                  name="preferred_date"
                  value={formData.preferred_date}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="preferred-time" className="block text-sm font-semibold text-gray-700 mb-2">
                  Tercih Edilen Saat
                </label>
                <select
                  id="preferred-time"
                  name="preferred_time"
                  value={formData.preferred_time}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 text-sm pr-8 disabled:bg-gray-50 disabled:cursor-not-allowed"
                >
                  <option value="">Saat seçin</option>
                  <option value="09:00">09:00</option>
                  <option value="10:00">10:00</option>
                  <option value="11:00">11:00</option>
                  <option value="12:00">12:00</option>
                  <option value="13:00">13:00</option>
                  <option value="14:00">14:00</option>
                  <option value="15:00">15:00</option>
                  <option value="16:00">16:00</option>
                  <option value="17:00">17:00</option>
                  <option value="18:00">18:00</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="appointment-message" className="block text-sm font-semibold text-gray-700 mb-2">
                Mesaj
              </label>
              <textarea
                id="appointment-message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={3}
                maxLength={500}
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 resize-none text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="Projeniz hakkında kısa bilgi verin..."
              ></textarea>
              <div className="text-right text-sm text-gray-500 mt-1">
                {formData.message.length}/500
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Randevu Alınıyor...
                </>
              ) : (
                <>
                  Randevu Al
                  <i className="ri-calendar-check-line ml-2"></i>
                </>
              )}
            </button>
          </form>

          {/* Info */}
          <div className="mt-8 p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl">
            <div className="flex items-center mb-3">
              <i className="ri-time-line text-blue-600 text-xl mr-2"></i>
              <span className="font-semibold text-gray-900">Randevu Bilgileri</span>
            </div>
            <p className="text-sm text-gray-600">
              Randevunuz onaylandıktan sonra size e-posta ve telefon ile bilgi verilecektir. Ortalama görüşme süresi 30-45 dakikadır.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoModal;
